import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { paths } from '@/routes/paths'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { fError } from '@/utils/format-error'
import { type Arena } from '@/types/arena.type'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/routes/hooks/use-router'
import { zodResolver } from '@hookform/resolvers/zod'
import axiosInstance, { endpoints } from '@/lib/axios'
import RichTextEditor from '@/components/ui/rich-text-editor'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MultipleMediaUpload } from '@/components/share-components/multiple-media-upload'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  type MediaImages,
  uploadGalleryImagesOnly,
  deleteMarkedMediaFiles,
} from '@/utils/upload-media-helper'

interface ArenaFormProps {
  arena?: Arena
  mode: 'create' | 'edit'
}

const arenaFormSchema = z.object({
  name: z.string().min(1, 'กรุณากรอกชื่อสนาม'),
  contactNumber: z.string().optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
})

type FormData = z.infer<typeof arenaFormSchema>

export default function ArenaForm({ arena, mode }: ArenaFormProps) {
  const router = useRouter()

  const form = useForm<FormData>({
    resolver: zodResolver(arenaFormSchema),
    defaultValues: {
      name: arena?.name || '',
      contactNumber: arena?.contactNumber || '',
      description: arena?.description || '',
    },
  })

  const [images, setImages] = useState<MediaImages>({
    gallery:
      arena?.mediaUrls?.map((media) => ({
        url: media.url,
        fileName: media.name,
        type: media.type,
        size: media.size,
      })) || [],
  })
  const [deletedMediaUrls, setDeletedMediaUrls] = useState<string[]>([])

  const handleBack = () => {
    router.push(paths.arenas)
  }

  const onSubmit = async (values: FormData) => {
    try {
      // Upload images using helper function
      const uploadedImages = await uploadGalleryImagesOnly(images, 'arenas')

      // Prepare arena data
      const arenaData = {
        ...values,
        ...uploadedImages,
      }

      if (mode === 'create') {
        await axiosInstance.post(endpoints.arenas.create, arenaData)
        toast.success('สร้างสนามสำเร็จ')
      } else {
        await axiosInstance.patch(
          endpoints.arenas.update((arena as Arena).id),
          arenaData,
        )
        toast.success('บันทึกสนามสำเร็จ')
      }

      // Delete media files that were marked for deletion (only in edit mode)
      if (mode === 'edit' && deletedMediaUrls.length > 0) {
        await deleteMarkedMediaFiles(deletedMediaUrls)
      }

      router.push(paths.arenas)
    } catch (error) {
      toast.error(fError(error))
    }
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          id="arena-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <Card className="border rounded-lg">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-black">
                ข้อมูลสนาม
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-black">
                        ชื่อสนาม
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="เช่น สนามไก่ชนบ้านนา"
                          className="rounded-lg border"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-black">
                        เบอร์ติดต่อ
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="เช่น 0812345678"
                          className="rounded-lg border"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel className="text-sm font-medium text-black">
                        คำอธิบาย
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="รายละเอียดของสนาม"
                          className="min-h-[200px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <MultipleMediaUpload
            title="รูปภาพสนาม"
            uploadedFiles={images.gallery}
            onFilesChange={(newGallery) =>
              setImages((prev) => ({ ...prev, gallery: newGallery }))
            }
            onFileDelete={(deletedUrl) => {
              setImages((prev) => ({
                ...prev,
                gallery: prev.gallery.filter((f) => f.url !== deletedUrl),
              }))
              // Track deleted URLs for cleanup in edit mode
              if (mode === 'edit' && typeof deletedUrl === 'string') {
                setDeletedMediaUrls((prev) => [...prev, deletedUrl])
              }
            }}
            maxFiles={10}
            maxSize={50}
            acceptedTypes={['image/*']}
            columns={2}
          />

          <div className="flex gap-3 py-6 border-t">
            <Button type="submit">
              {mode === 'create' ? 'สร้างสนาม' : 'บันทึกการแก้ไข'}
            </Button>
            <Button type="button" variant="outline" onClick={handleBack}>
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
